import { ArrowLeft, CheckCircle2, CircleDashed, CircleSlash, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import { cn } from '~/utils/cn';
import { formatDateTime } from '~/utils/date';

import { useFunnelProductTrace } from '../../hooks/use-batch-funnel';
import type { FunnelTraceResult } from '../../types/batch-funnel.api.types';

/** Step result → icon + colour token. Unknown results fall back to the dashed circle. */
function resultVisual(result: FunnelTraceResult | null) {
  switch (result) {
    case 'SUCCEEDED':
      return { Icon: CheckCircle2, className: 'text-success' };
    case 'FAILED':
      return { Icon: XCircle, className: 'text-error' };
    case 'SKIPPED':
      return { Icon: CircleSlash, className: 'text-muted-foreground' };
    default:
      return { Icon: CircleDashed, className: 'text-muted-foreground' };
  }
}

interface FunnelProductTraceProps {
  jobId: string;
  productId: string;
  onBack: () => void;
}

/**
 * One product's journey through the batch (endpoint 3): a vertical step list
 * with result, time, attempt, code / message and the agent detail line. Live
 * destination state is not in the trace — `unavailable` says where it lives.
 */
export function FunnelProductTrace({ jobId, productId, onBack }: FunnelProductTraceProps) {
  const { t } = useTranslation('catalog-operations');
  const { data, isLoading, isError } = useFunnelProductTrace(jobId, productId);

  return (
    <div className="space-y-4" data-testid="batch-funnel-trace">
      <Button
        variant="ghost"
        size="sm"
        className="cursor-pointer gap-1 px-2"
        onClick={onBack}
        data-testid="batch-funnel-trace-back-button"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('batchFunnel.drill.backToList')}
      </Button>

      {isLoading ? (
        <div className="space-y-2" data-testid="batch-funnel-trace-loading">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : isError || !data ? (
        <p className="text-error text-sm" data-testid="batch-funnel-trace-error">
          {t('batchFunnel.drill.traceError')}
        </p>
      ) : (
        <>
          <div className="space-y-0.5">
            <p
              className="text-foreground font-semibold break-words"
              data-testid="batch-funnel-trace-title"
            >
              {data.title ?? data.externalId ?? data.productId}
            </p>
            {data.externalId && (
              <p className="text-muted-foreground font-mono text-xs break-all">{data.externalId}</p>
            )}
            {data.finalOutcome && (
              <p className="text-muted-foreground text-sm">
                {t('batchFunnel.drill.finalOutcome')}{' '}
                <span
                  className="text-foreground font-medium"
                  data-testid="batch-funnel-trace-final-outcome-value"
                >
                  {t(`batchFunnel.drill.finalOutcomeValue.${data.finalOutcome}`, {
                    defaultValue: data.finalOutcome,
                  })}
                </span>
              </p>
            )}
          </div>

          <ol className="space-y-3" data-testid="batch-funnel-trace-step-list">
            {(data.steps ?? []).map((step, index) => {
              const { Icon, className } = resultVisual(step.result);
              return (
                <li
                  key={`${step.stage ?? ''}:${String(index)}`}
                  className="flex gap-3"
                  data-testid={`batch-funnel-trace-step-${step.stage ?? 'unknown'}-${String(index)}`}
                >
                  <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', className)} aria-hidden="true" />
                  <div className="min-w-0 flex-1 space-y-0.5 text-sm">
                    <p className="text-foreground font-medium">
                      {step.stage}
                      <span className="text-muted-foreground font-normal">
                        {' · '}
                        {t(`batchFunnel.drill.stepResult.${step.result ?? 'UNKNOWN'}`, {
                          defaultValue: step.result ?? '—',
                        })}
                      </span>
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {[
                        step.at ? formatDateTime(step.at) : null,
                        step.attempt != null
                          ? t('batchFunnel.drill.attempt', { count: step.attempt })
                          : null,
                        step.code,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                    {step.message && <p className="text-foreground break-words">{step.message}</p>}
                    {step.detail && (
                      <p className="text-muted-foreground font-mono text-xs break-all">
                        {step.detail}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>

          {(data.unavailable ?? []).filter((u) => u.reason).length > 0 && (
            <ul className="text-muted-foreground list-disc space-y-0.5 pl-5 text-xs">
              {(data.unavailable ?? []).map((u, index) =>
                u.reason ? <li key={`${u.field ?? ''}:${String(index)}`}>{u.reason}</li> : null,
              )}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
